import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { components, StylesConfig } from 'react-select';
import { useTranslation } from 'react-i18next';
import { CapsulePropertyOutputV1, PropertyOutputV1, ScalarPropertyV1, sqItemsApi } from '@/sdk';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { Icon } from '@/hybrid/core/Icon.atom';
import { fetchConditionProperties } from '@/hybrid/utilities/investigateHelper.utilities';
import { COLOR_COLUMN_NAME, PRIORITY_COLUMN_NAME } from '@/hybrid/tableBuilder/tableBuilder.constants';
import { ADVANCED_CAPSULE_PROPERTIES, ADVANCED_PROPERTIES, PropertyColumn } from '@/trendData/trendData.constants';

export enum SuggestedPropertiesMode {
  Series = 'series',
  Condition = 'condition',
  Capsules = 'capsules',
}

export interface CustomPropertySelectorProps {
  itemIds: string[];
  customStyles?: StylesConfig<any, any>;
  dropdownPlaceholder: string;
  addPropertyColumn: (column: { propertyName: string; style?: string }, isCapsuleProperty?: boolean) => void;
  excludedProperties?: string[];
  extraProperties?: PropertyColumn[];
  suggestedPropertiesMode?: SuggestedPropertiesMode;
}

// Properties that are on the underlying backing condition of metrics and thus aren't useful to the user
export const METRIC_CONDITION_PROPERTIES = [COLOR_COLUMN_NAME, PRIORITY_COLUMN_NAME];

// Properties on a condition that are not included in the list of properties returned by the API
const EXTRA_CONDITION_PROPERTIES = [SeeqNames.Properties.Id];

/**
 * Dropdown with selectable, searchable list of item properties
 */
export const CustomPropertySelector: React.FunctionComponent<CustomPropertySelectorProps> = ({
  itemIds,
  addPropertyColumn,
  customStyles,
  dropdownPlaceholder,
  excludedProperties = [],
  extraProperties = [],
  suggestedPropertiesMode = SuggestedPropertiesMode.Series,
}) => {
  const { t } = useTranslation();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  // Needed so that user can click outside the dropdown and have it close
  const menuDropdownClickHandler = useRef<EventListenerOrEventListenerObject>((event) => {
    if (!(event.target as HTMLDivElement)?.classList.contains('react-select__option')) {
      setMenuIsOpen(false);
    }
  });

  const [capsuleProperties, setCapsuleProperties] = useState<string[]>([]);
  type PropertyOption = { value: string; label: string; style: string | undefined };
  const [propertyOptions, setPropertyOptions] = useState<PropertyOption[]>([]);
  useEffect(() => {
    fetchProperties();
    document.addEventListener('click', menuDropdownClickHandler.current);
    return () => {
      document.removeEventListener('click', menuDropdownClickHandler.current);
    };
  }, []);

  /**
   * Fetches a list of possible properties from the specified items, formats them to be options for the dropdown,
   * and appends an extra properties. If a request fails, then the request will fail silently
   */
  function fetchProperties() {
    return Promise.resolve()
      .then(() =>
        suggestedPropertiesMode === SuggestedPropertiesMode.Series
          ? fetchItemProperties()
          : fetchCapsuleAndConditionProperties(),
      )
      .then((properties) => {
        _.chain(properties)
          .reject((property) =>
            _.chain(excludedProperties)
              .concat(suggestedPropertiesMode === SuggestedPropertiesMode.Capsules ? [] : ADVANCED_PROPERTIES)
              .concat(suggestedPropertiesMode === SuggestedPropertiesMode.Series ? [] : ADVANCED_CAPSULE_PROPERTIES)
              .without(..._.map(extraProperties, valueForPropertyOption))
              .includes(property.name)
              .value(),
          )
          .map(
            (property): PropertyOption => ({
              value: property.name,
              label: property.name,
              style: property.unitOfMeasure,
            }),
          )
          .concat(
            _.map(
              extraProperties,
              (property): PropertyOption => ({
                value: valueForPropertyOption(property),
                label: labelForExtraProperty(property),
                style: undefined,
              }),
            ),
          )
          .uniqBy('value')
          .sortBy('label')
          .thru((options) => setPropertyOptions(options))
          .value();
      });
  }

  /**
   * Fetch properties for all items.
   */
  function fetchItemProperties(): Promise<CapsulePropertyOutputV1[]> {
    return _.chain(itemIds)
      .map((id) => sqItemsApi.getItemAndAllProperties({ id }))
      .thru((results) => Promise.all(results))
      .value()
      .then((responses) => _.flatMap(responses, (r) => propertiesToCapsuleProperties(r.data.properties)));
  }

  /**
   * Fetch condition and capsule properties for all conditions.
   */
  function fetchCapsuleAndConditionProperties(): Promise<CapsulePropertyOutputV1[]> {
    return _.chain(itemIds)
      .map((id) => fetchConditionProperties(id))
      .thru((results) => Promise.all(results))
      .value()
      .then((responses) => {
        const capsuleProperties = _.flatMap(responses, (r) => r.capsuleProperties);
        setCapsuleProperties(
          _.chain(capsuleProperties)
            .map('name')
            .concat(_.map(extraProperties, valueForPropertyOption))
            .uniq()
            .value() as string[],
        );
        if (suggestedPropertiesMode === SuggestedPropertiesMode.Condition) {
          const conditionProperties: CapsulePropertyOutputV1[] = _.flatMap(responses, ({ conditionProperties }) =>
            propertiesToCapsuleProperties(conditionProperties),
          );
          return conditionProperties
            .concat(_.map(EXTRA_CONDITION_PROPERTIES, (name) => ({ name })))
            .concat(capsuleProperties);
        } else {
          return capsuleProperties;
        }
      });
  }

  function valueForPropertyOption(property: PropertyColumn): string {
    return _.isString(property.propertyName) ? property.propertyName : property.key;
  }

  function labelForExtraProperty(property: PropertyColumn): string {
    if (_.isString(property.propertyName)) {
      return property.propertyName;
    } else if (_.isString(property.title)) {
      return t(property.title);
    } else {
      return property.key;
    }
  }

  function propertiesToCapsuleProperties(
    properties: (ScalarPropertyV1 | PropertyOutputV1)[],
  ): CapsulePropertyOutputV1[] {
    return _.map(properties, (property) => ({
      name: property.name,
      unitOfMeasure: property.unitOfMeasure,
    }));
  }

  // region keep menu open so that user can select several options without opening the menu again
  // @ts-ignore: Getting the types to work correctly with these react-select components isn't worth it
  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          {menuIsOpen && <Icon icon="fa fa-caret-up" large={true} />}
          {!menuIsOpen && <Icon icon="fa fa-caret-down" large={true} />}
        </components.DropdownIndicator>
      )
    );
  };

  // @ts-ignore: Getting the types to work correctly with these react-select components isn't worth it
  const IndicatorsContainer = (props) => {
    return (
      components.IndicatorsContainer && (
        <div
          onClick={(e) => {
            setMenuIsOpen(!menuIsOpen);
            // We have to stop the event from propagating because otherwise angular popovers will interpret a click
            // inside this selector as being outside of the popover (and close the popover immediately).
            e.stopPropagation();
          }}>
          <components.IndicatorsContainer {...props} />
        </div>
      )
    );
  };

  // @ts-ignore: Getting the types to work correctly with these react-select components isn't worth it
  const ValueContainer = (props) => {
    return (
      components.ValueContainer && (
        <div
          className="flexFill"
          onClick={(e) => {
            setMenuIsOpen(!menuIsOpen);
            // We have to stop the event from propagating because otherwise angular popovers will interpret a click
            // inside this selector as being outside of the popover (and close the popover immediately).
            e.stopPropagation();
          }}>
          <components.ValueContainer {...props} />
        </div>
      )
    );
  };
  // endregion

  const addProperty = (column: PropertyOption) => {
    addPropertyColumn({ propertyName: column.value, style: column.style }, _.includes(capsuleProperties, column.label));
  };

  return (
    <div data-testid="customPropertySelector">
      <CreatableSelect
        components={{ ValueContainer, IndicatorsContainer, DropdownIndicator }}
        menuIsOpen={menuIsOpen}
        styles={customStyles}
        // Shows the placeholder value and clears the value after adding the property column
        value={null}
        autoFocus={true}
        isSearchable={true}
        className="flexFillOverflow"
        classNamePrefix="react-select"
        placeholder={t(dropdownPlaceholder)}
        options={propertyOptions}
        inputId="customPropertyInput"
        onChange={addProperty}
      />
    </div>
  );
};
